import { create } from 'zustand';
import { message } from 'antd';
import {
    getAllCampaigns, updateCampaign, deleteCampaign,
    getCampaignsV3, getCampaignV3, getCampaignContactsV3,
    putCampaignOnHold as putOnHoldApi,
    resumeCampaign as resumeApi,
    rescheduleCampaign as rescheduleApi
} from '../reducers/campaign/resources';
import session from '../utils/session';

function determineCampaignState(details) {
    if (!details) return null;
    if (details.is_draft) return 'draft';
    if (details.is_inprogress) return 'ongoing';
    if (details.status === false) return 'scheduled';
    if (details.status === null) return 'on_hold';
    return 'sent';
}

const useCampaignsStore = create((set, get) => ({
    // Data
    loading: false,
    campaignState: null,
    autoRefreshInterval: null,
    
    // Pagination & Filtering state
    campaigns: [], // Now holds only current page rows
    filteredCampaigns: [], // Deprecated, but keeping for compatibility if component uses it, alias to campaigns
    pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
    },

    // Filters
    filters: {
        search: '',
        status: 'all', // all, sent, scheduled, sending, on_hold
    },

    // gh-admin only: filter the list to a single company picked in the top-bar selector.
    // null = no company filter (admin sees all campaigns).
    companyFilter: null,

    // Sort (server-side)
    sort: {
        field: 'created_at', // one of: name, created_at, schedule, creator_name
        order: 'desc'
    },

    // Actions
    setFilters: (newFilters) => {
        set((state) => ({
            filters: { ...state.filters, ...newFilters },
            pagination: { ...state.pagination, page: 1 }
        }));
        get().fetchCampaigns();
    },

    // Driven by the top-bar company selector (gh-admin). Resets to page 1 and refetches.
    setCompanyFilter: (companyId) => {
        const next = companyId || null;
        set((state) => ({
            companyFilter: next,
            pagination: { ...state.pagination, page: 1 }
        }));
        get().fetchCampaigns();
    },

    setSort: (field, order) => {
        set((state) => ({
            sort: { field, order },
            pagination: { ...state.pagination, page: 1 }
        }));
        // Intentionally no fetch here — callers combine this with setPage which fetches.
    },

    // V2 Viewer State
    campaignDetails: null, // Holds the single campaign details linked to getCampaignV3
    contactsLoading: false,
    campaignContacts: [],
    contactsPagination: {
        page: 1,
        pageSize: 20,
        total: 0
    },
    contactsFilters: {
        search: '',
        status: 'all',
        sort: 'name',
        order: 'asc'
    },

    // V2 Actions
    setContactsFilters: (newFilters) => {
        set((state) => ({
            contactsFilters: { ...state.contactsFilters, ...newFilters },
            contactsPagination: { ...state.contactsPagination, page: 1 }
        }));
        get().fetchCampaignContacts();
    },

    setContactsPage: (page, pageSize) => {
        set((state) => ({
            contactsPagination: { ...state.contactsPagination, page, pageSize }
        }));
        get().fetchCampaignContacts();
    },

    fetchCampaignDetails: async (id) => {
        const current = get().campaignDetails;
        const isNewCampaign = !current || current.id !== id;
        if (isNewCampaign) {
            set({ loading: true, campaignDetails: null, campaignState: null, campaignContacts: [] });
        }
        try {
            const res = await getCampaignV3(id);
            set({
                campaignDetails: res.data,
                campaignState: determineCampaignState(res.data),
                loading: false
            });
        } catch (error) {
            console.error('Failed to fetch campaign details:', error);
            set({ loading: false });
            message.error('Failed to load campaign details');
        }
    },

    fetchCampaignContacts: async (id) => {
        // ID is optional, if not provided use current campaignDetails.id
        const currentId = id || (get().campaignDetails || {}).id;
        if (!currentId) return;

        set({ contactsLoading: true });
        const { contactsPagination, contactsFilters } = get();

        try {
            const params = {
                page: contactsPagination.page,
                limit: contactsPagination.pageSize,
                search: contactsFilters.search,
                status: contactsFilters.status,
                sort: contactsFilters.sort,
                order: contactsFilters.order
            };

            const res = await getCampaignContactsV3(currentId, params);
            const { rows, total, page, pageSize } = res.data;

            set({
                campaignContacts: rows,
                contactsPagination: {
                    page: parseInt(page),
                    pageSize: parseInt(pageSize),
                    total: parseInt(total)
                },
                contactsLoading: false
            });
        } catch (error) {
             console.error('Failed to fetch contacts:', error);
             set({ contactsLoading: false });
             message.error('Failed to load contacts');
        }
    },

    setPage: (page, pageSize) => {
        set((state) => ({
            pagination: { ...state.pagination, page, pageSize }
        }));
        get().fetchCampaigns();
    },

    fetchCampaigns: async () => {
        set({ loading: true });
        const { pagination, filters, sort, companyFilter } = get();

        try {
            const params = {
                page: pagination.page,
                limit: pagination.pageSize,
                search: filters.search,
                status: filters.status,
                sort: sort.field,
                order: sort.order
            };

            // gh-admin: scope the list to the company picked in the top-bar selector.
            if (companyFilter) {
                params.companyId = companyFilter;
            }

            const res = await getCampaignsV3(params);
            const { rows, total, page, pageSize } = res.data;

            set({ 
                campaigns: rows, 
                filteredCampaigns: rows, // Alias for component
                pagination: {
                    page: parseInt(page),
                    pageSize: parseInt(pageSize),
                    total: parseInt(total)
                },
                loading: false 
            });

        } catch (error) {
            console.error('Failed to fetch campaigns:', error);
            set({ loading: false });
            message.error('Failed to load campaigns');
        }
    },
    
    // processCampaigns removed as it is handled by backend now

    updateCampaignStatus: async (id, isActive) => {
        try {
            await updateCampaign(id, { is_active: isActive });
            message.success('Campaign updated');
            get().fetchCampaigns();
        } catch (error) {
            message.error('Failed to update campaign');
        }
    },

    deleteCampaign: async (id) => {
        try {
            await deleteCampaign(id);
            message.success('Campaign deleted');
            get().fetchCampaigns();
        } catch (error) {
             console.error(error)
             message.error('Failed to delete campaign');
        }
    },

    // Auto-refresh for ongoing campaigns
    startAutoRefresh: (campaignId, intervalMs = 15000) => {
        get().stopAutoRefresh();
        const interval = setInterval(() => {
            get().fetchCampaignDetails(campaignId);
            get().fetchCampaignContacts(campaignId);
        }, intervalMs);
        set({ autoRefreshInterval: interval });
    },

    stopAutoRefresh: () => {
        const { autoRefreshInterval } = get();
        if (autoRefreshInterval) {
            clearInterval(autoRefreshInterval);
            set({ autoRefreshInterval: null });
        }
    },

    // Campaign actions for scheduled campaigns
    putCampaignOnHold: async (id) => {
        try {
            await putOnHoldApi(id);
            message.success('Campaign put on hold');
            get().fetchCampaignDetails(id);
        } catch (error) {
            console.error(error);
            message.error('Failed to put campaign on hold');
        }
    },

    resumeCampaign: async (id) => {
        try {
            await resumeApi(id);
            message.success('Campaign resumed');
            get().fetchCampaignDetails(id);
        } catch (error) {
            console.error(error);
            message.error('Failed to resume campaign');
        }
    },

    rescheduleCampaign: async (id, schedule, timezone) => {
        try {
            await rescheduleApi(id, schedule, timezone);
            message.success('Campaign rescheduled');
            get().fetchCampaignDetails(id);
        } catch (error) {
            console.error(error);
            message.error('Failed to reschedule campaign');
        }
    }
}));

export default useCampaignsStore;
